import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { Sparkles, MapPin, Phone, ArrowUpRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { businessInfo } from '../mockData';

const PolishScene3D = lazy(() =>
  import('./PolishScene3D').then((m) => ({ default: m.PolishScene3D }))
);

gsap.registerPlugin(ScrollTrigger);

export const Hero = () => {
  const heroRef = useRef(null);
  const eyebrowRef = useRef(null);
  const stilettoRef = useRef(null);
  const scriptRef = useRef(null);
  const dividerRef = useRef(null);
  const taglineRef = useRef(null);
  const ctaRef = useRef(null);
  const metaRef = useRef(null);
  const sceneWrapRef = useRef(null);
  const sideMetaRef = useRef(null);
  const marqueeRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.from(eyebrowRef.current, { y: -20, opacity: 0, duration: 0.7 })
        .from(
          stilettoRef.current,
          {
            y: 120,
            opacity: 0,
            duration: 1.4,
            ease: 'power4.out',
            clipPath: 'inset(100% 0 0 0)',
          },
          '-=0.3'
        )
        .from(
          scriptRef.current,
          { x: -60, opacity: 0, duration: 1.1, ease: 'power3.out' },
          '-=0.9'
        )
        .from(dividerRef.current, { scaleX: 0, duration: 0.9, ease: 'expo.out' }, '-=0.6')
        .from(taglineRef.current, { y: 30, opacity: 0, duration: 0.8 }, '-=0.5')
        .from(
          ctaRef.current?.children || [],
          { y: 20, opacity: 0, duration: 0.6, stagger: 0.1 },
          '-=0.5'
        )
        .from(
          metaRef.current?.children || [],
          { y: 15, opacity: 0, duration: 0.5, stagger: 0.08 },
          '-=0.3'
        )
        .from(
          sceneWrapRef.current,
          { opacity: 0, scale: 0.85, duration: 1.6, ease: 'power3.out' },
          '-=1.8'
        )
        .from(
          sideMetaRef.current?.children || [],
          { y: 20, opacity: 0, duration: 0.5, stagger: 0.08 },
          '-=0.6'
        );

      // Parallax — scene drifts up slowly while text drifts opposite
      gsap.to(sceneWrapRef.current, {
        yPercent: 25,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        },
      });

      gsap.to([stilettoRef.current, scriptRef.current], {
        yPercent: -30,
        opacity: 0.4,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5,
        },
      });

      // Marquee infinite loop
      if (marqueeRef.current) {
        const inner = marqueeRef.current.querySelector('.marquee-inner');
        if (inner) {
          const width = inner.scrollWidth / 2;
          gsap.to(inner, {
            x: -width,
            duration: 28,
            repeat: -1,
            ease: 'none',
          });
        }
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const scrollToBooking = () =>
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToServices = () =>
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: '#FAF6F2' }}
    >
      {/* Subtle paper grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Wash of color in corners */}
      <div
        className="absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none opacity-30"
        style={{ background: 'radial-gradient(circle, #FFD9E5 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] rounded-full blur-3xl pointer-events-none opacity-25"
        style={{ background: 'radial-gradient(circle, #FF2D87 0%, transparent 70%)' }}
      />

      {/* Vertical side label (left) */}
      <div
        ref={sideMetaRef}
        className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col gap-6 z-20"
        style={{ writingMode: 'vertical-rl' }}
      >
        <span
          data-testid="hero-side-label-est"
          className="font-mono-ed text-[10px] tracking-[0.3em] uppercase text-[#1A0B0E]/60 rotate-180"
        >
          Est · Miami Beach
        </span>
        <span
          className="font-mono-ed text-[10px] tracking-[0.3em] uppercase text-[#1A0B0E]/60 rotate-180"
        >
          Women + Latino Owned
        </span>
      </div>

      {/* Vertical side label (right) */}
      <div
        className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-6 z-20"
        style={{ writingMode: 'vertical-rl' }}
      >
        <span
          className="font-mono-ed text-[10px] tracking-[0.3em] uppercase text-[#1A0B0E]/60"
        >
          ✦ 4.8 / 5 · 38 Reviews
        </span>
      </div>

      {/* 3D Scene — anchored right side */}
      <div
        ref={sceneWrapRef}
        data-testid="hero-3d-wrap"
        className="absolute top-0 right-0 bottom-0 z-0 w-full lg:w-[58%]"
      >
        <Suspense fallback={<div className="w-full h-full" />}>
          <PolishScene3D />
        </Suspense>
      </div>

      {/* Soft gradient mask so text stays readable */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'linear-gradient(90deg, rgba(250,246,242,1) 0%, rgba(250,246,242,0.92) 25%, rgba(250,246,242,0.5) 42%, rgba(250,246,242,0.1) 55%, rgba(250,246,242,0) 65%)',
        }}
      />

      {/* Main editorial content */}
      <div className="relative z-10 container mx-auto px-6 lg:px-20 min-h-screen flex flex-col justify-center pt-24 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left text column — 7 cols on lg */}
          <div className="lg:col-span-7 max-w-2xl">
            {/* Eyebrow */}
            <div
              ref={eyebrowRef}
              data-testid="hero-eyebrow"
              className="flex items-center gap-3 mb-8"
            >
              <div className="h-px w-10 bg-[#1A0B0E]/40" />
              <span className="font-mono-ed text-[11px] tracking-[0.25em] uppercase text-[#1A0B0E]/70">
                A Miami Beach Salon · Since 2019
              </span>
            </div>

            {/* Big serif "STILETTO" */}
            <h1 className="leading-[0.85] mb-4">
              <span
                ref={stilettoRef}
                data-testid="hero-title-stiletto"
                className="block font-serif-ed font-black text-[#1A0B0E] text-[clamp(4rem,12vw,11rem)] tracking-[-0.04em]"
                style={{ display: 'inline-block' }}
              >
                Stiletto
              </span>
              <span
                ref={scriptRef}
                data-testid="hero-title-script"
                className="block font-script text-[#FF2D87] text-[clamp(3rem,8vw,7rem)] leading-none -mt-2 lg:-mt-6 italic pl-2"
              >
                nails on the beach
              </span>
            </h1>

            <div ref={dividerRef} className="h-[2px] w-32 bg-[#1A0B0E] my-8 origin-left" />

            <p
              ref={taglineRef}
              data-testid="hero-tagline"
              className="font-serif-ed italic text-lg md:text-xl text-[#1A0B0E]/80 max-w-md mb-10 leading-relaxed"
            >
              Where pink roses meet neon glow — Miami's atelier for hands that
              <span className="text-[#FF2D87]"> turn heads</span>.
            </p>

            {/* CTAs — editorial style, not pill buttons */}
            <div ref={ctaRef} className="flex flex-col sm:flex-row items-start gap-6 mb-12">
              <button
                data-testid="hero-book-btn"
                onClick={scrollToBooking}
                className="group inline-flex items-center gap-3 bg-[#1A0B0E] text-[#FAF6F2] pl-7 pr-2 py-2 rounded-full hover:bg-[#FF2D87] transition-colors duration-500"
              >
                <span className="text-sm tracking-wide uppercase font-medium">
                  Book an Appointment
                </span>
                <span className="bg-[#FF2D87] group-hover:bg-[#1A0B0E] text-[#FAF6F2] rounded-full p-2.5 transition-colors duration-500">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>

              <button
                data-testid="hero-services-btn"
                onClick={scrollToServices}
                className="group inline-flex items-center gap-2 text-[#1A0B0E] hover:text-[#FF2D87] transition-colors py-3"
              >
                <span className="text-sm tracking-wide uppercase border-b border-[#1A0B0E] group-hover:border-[#FF2D87] pb-0.5">
                  View Service Menu
                </span>
              </button>
            </div>

            {/* Meta info row */}
            <div
              ref={metaRef}
              className="flex flex-wrap items-center gap-6 text-[#1A0B0E]/70"
            >
              <div
                data-testid="hero-location-pill"
                className="flex items-center gap-2"
              >
                <MapPin className="w-4 h-4 text-[#FF2D87]" />
                <span className="font-mono-ed text-xs tracking-wider">
                  7449 COLLINS AVE
                </span>
              </div>
              <div className="h-3 w-px bg-[#1A0B0E]/30" />
              <div
                data-testid="hero-phone-pill"
                className="flex items-center gap-2"
              >
                <Phone className="w-4 h-4 text-[#FF2D87]" />
                <span className="font-mono-ed text-xs tracking-wider">
                  786 · 423 · 3723
                </span>
              </div>
              <div className="h-3 w-px bg-[#1A0B0E]/30" />
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF2D87]" />
                <span className="font-mono-ed text-xs tracking-wider">
                  WALK-INS WELCOME
                </span>
              </div>
            </div>
          </div>

          {/* Right column — empty, 3D scene occupies via absolute */}
          <div className="lg:col-span-5 hidden lg:block" />
        </div>
      </div>

      {/* Bottom marquee */}
      <div
        ref={marqueeRef}
        data-testid="hero-marquee"
        className="absolute bottom-0 left-0 right-0 border-t border-[#1A0B0E]/20 bg-[#FAF6F2]/70 backdrop-blur-sm py-4 z-20 overflow-hidden"
      >
        <div className="marquee-inner flex whitespace-nowrap gap-12">
          {Array.from({ length: 2 }).map((_, i) => (
            <React.Fragment key={i}>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Russian Manicure</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Gel X Apres</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Luxury Volcano Pedicure</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Builder Gel</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Jelly SPA Bath</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
              <span className="font-serif-ed italic text-2xl text-[#1A0B0E]">Custom Nail Art</span>
              <span className="font-mono-ed text-xs tracking-[0.3em] text-[#FF2D87] self-center">✦</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
